import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Radar, LayoutDashboard, Building2, Activity, SlidersHorizontal, PlugZap, Settings, LogOut, Menu, Archive, Bot,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "analyst", "sales", "readonly"] },
  { to: "/companies", label: "Companies", icon: Building2, roles: ["admin", "analyst", "sales", "readonly"] },
  { to: "/activity", label: "Lead Activity", icon: Activity, roles: ["admin", "analyst", "sales", "readonly"] },
  { to: "/scoring", label: "Scoring Admin", icon: SlidersHorizontal, roles: ["admin"] },
  { to: "/connectors", label: "Source Policies", icon: PlugZap, roles: ["admin"] },
  { to: "/agents", label: "Agent Control Centre", icon: Bot, roles: ["admin", "analyst", "sales", "readonly"] },
  { to: "/retention", label: "Retention", icon: Archive, roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, roles: ["admin", "analyst", "sales", "readonly"] },
];

const ROLE_LABEL: Record<string, string> = { admin: "Admin", analyst: "Analyst", sales: "Sales", readonly: "Read-only" };

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [synthetic, setSynthetic] = useState(false);
  useEffect(() => {
    api.get("/app/status").then((r) => setSynthetic(!!r.data?.synthetic_active)).catch(() => {});
  }, []);
  const visibleNav = NAV.filter((n) => !user || n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top nav */}
      <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn"><Menu className="h-5 w-5" /></Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72" data-testid="mobile-menu">
                <div className="flex items-center gap-2 mb-6">
                  <div className="h-8 w-8 rounded-md bg-[#0B1C3D] flex items-center justify-center"><Radar className="h-5 w-5 text-teal-400" /></div>
                  <span className="font-heading font-bold text-slate-900">DefencIQ Radar</span>
                </div>
                <nav className="flex flex-col gap-1">
                  {visibleNav.map((n) => (
                    <NavLink key={n.to} to={n.to} onClick={() => setMenuOpen(false)}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) => cn("flex items-center gap-2 px-3 py-2.5 rounded-md text-sm",
                        isActive ? "bg-teal-50 text-teal-800 font-semibold" : "text-slate-600 hover:bg-slate-50")}>
                      <n.icon className="h-4 w-4" /> {n.label}
                    </NavLink>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
            <div className="flex items-center gap-2" data-testid="brand">
              <div className="h-8 w-8 rounded-md bg-[#0B1C3D] flex items-center justify-center">
                <Radar className="h-5 w-5 text-teal-400" />
              </div>
              <div className="leading-tight">
                <div className="font-heading font-bold text-slate-900 text-[15px]">DefencIQ Radar</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Defence-market intelligence</div>
              </div>
            </div>
            <nav className="hidden lg:flex items-center gap-1">
              {visibleNav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors",
                      isActive ? "bg-teal-50 text-teal-800 font-semibold" : "text-slate-600 hover:bg-slate-50"
                    )
                  }
                >
                  <n.icon className="h-4 w-4" /> {n.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium text-slate-800">{user?.name}</div>
              <div className="text-[11px] uppercase tracking-wide text-teal-700 font-semibold">{ROLE_LABEL[user?.role || ""]}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
            >
              <LogOut className="h-4 w-4 mr-1" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      {synthetic && (
        <div data-testid="synthetic-banner" className="bg-amber-500 text-amber-950">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
            <span className="inline-block h-2 w-2 rounded-full bg-amber-900 animate-pulse" />
            Synthetic Data — Non-production. Seed data is active; all records are fictional and for testing only.
          </div>
        </div>
      )}

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">{children}</main>
    </div>
  );
}
